import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";
import { useInstallerInfo, useSettings } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { CodeBlock } from "@/components/CodeBlock";
import { TerminalSquare, ArrowRight, Globe2 } from "lucide-react";

export default function InstallerPage() {
  const { data: info } = useInstallerInfo();
  const { data: settings } = useSettings();
  const [bootstrapSrc, setBootstrapSrc] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/installer/bootstrap.sh`)
      .then((r) => r.text())
      .then(setBootstrapSrc)
      .catch(() => setBootstrapSrc("# failed to load"));
  }, []);

  const base = settings?.base_domain || info?.base_domain || "emhost.local";
  const curl = `curl -fsSL ${window.location.origin}${API_BASE.replace(window.location.origin, "")}/installer/bootstrap.sh | sudo bash -s -- ${base}`;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Installer</h1>
        <p className="text-muted-foreground mt-1">
          Install emhost on a fresh Ubuntu VPS. Docker + Caddy are set up automatically.
        </p>
      </header>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-3">
          <TerminalSquare className="h-5 w-5" />
          <h2 className="text-lg font-semibold">One-liner install</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Run on the VPS you want to turn into your hosting node. Requires root.
        </p>
        <div data-testid="installer-curl-command">
          <CodeBlock label="install.sh" language="bash" code={curl}
                     testid="installer-curl-copy-button" maxHeight="120px" />
        </div>
      </Card>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-3">
          <Globe2 className="h-5 w-5" />
          <h2 className="text-lg font-semibold">DNS setup</h2>
        </div>
        <div data-testid="installer-dns-instructions">
          <ol className="space-y-3 text-sm">
            {(info?.dns_instructions || [
              "Buy or use an existing domain.",
              `Create a DNS A record for *.${base} pointing to your VPS IP.`,
              "Or create individual A records per subdomain.",
              "HTTPS is issued automatically by Caddy (Let's Encrypt).",
            ]).map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full surface-2 grid place-items-center text-[11px] shrink-0">{i + 1}</div>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight className="h-5 w-5" />
          <h2 className="text-lg font-semibold">bootstrap.sh preview</h2>
        </div>
        <div data-testid="installer-bootstrap-preview">
          <CodeBlock label="bootstrap.sh" language="bash" code={bootstrapSrc} maxHeight="460px" />
        </div>
      </Card>
    </div>
  );
}
